/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:no-magic-numbers

import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  Optional,
  Self,
  ViewChild,
} from '@angular/core';
import { State } from '@gh/common-shared';
import { STYLES_FLEX_COLUMN_LAYOUT, STYLES_FULL_HEIGHT, WaitingContext, WaitingMode } from '@gh/core-ui';
import { addDays, cloneDate, isNotNil, shiftDays } from '@gh/core-util';
import { fromEvent$ } from '@gh/rx';
import { clamp, isEmpty } from 'lodash';
import {
  GANTT_CHART_ACCESSOR,
  GANTT_CHART_CONTROLLER,
  GanttChartAccessor,
  GanttChartController,
  GanttChartTimelineDirective,
  GanttChartView,
} from '../../directives/gantt-chart-timeline.directive';
import { GanttState, queryGanttCurrentDate, queryGanttVisibleTaskSeq } from '../../gantt-chart.state';
import { GanttChartRestService } from '../../services/gantt-chart-rest.service';
import { ganttExportConfig, getScheduleConfig } from '../../resources/gantt-export-config';
import { GanttTask } from '../../resources/gantt.model';

const getNextScale = (scale: number, shift: number) => clamp(scale + shift / 10, 0.5, 2);

@Component({
             selector       : 'gh-gantt-chart-timeline',
             template: require('./gantt-chart-timeline.component.html'),
             styles         : [STYLES_FLEX_COLUMN_LAYOUT, STYLES_FULL_HEIGHT],
             changeDetection: ChangeDetectionStrategy.OnPush,
             // tslint:disable-next-line:use-host-property-decorator
             host: {
               '[class.m-template]': '!instance',
               '[class.m-instance]': 'instance',
             },
           })
export class GanttChartTimelineComponent implements AfterViewInit {
  @Input() title: string;
  // tslint:disable-next-line:no-input-prefix
  @Input() isTemplate?: boolean;
  // tslint:disable-next-line:no-input-prefix
  @Input() isCombined?: boolean;
  @Input() name: string;
  @ViewChild('gantt', { static: false }) ganttEl: ElementRef;

  private scale: number = 1;
  private _instance: boolean = false;

  @Input('ghGanttChartTimelineInstance')
  set instance(instance: boolean) {
    this._instance = coerceBooleanProperty(instance);
  }

  get instance(): boolean {
    return this._instance;
  }

  get hasTitle(): boolean {
    return !isEmpty(this.title);
  }

  get isDateView(): boolean {
    return this._instance;
  }

  get hasCurrentDate(): boolean {
    return isNotNil(this.state.project(queryGanttCurrentDate));
  }

  // It is necessary to use @Optional() for timeline dependency,
  // because otherwise Angular throws errors in AoT mode (No provider for GanttChartTimelineDirective).
  // It seems like bug of Angular,
  // this solution can be reconsidered in future after migration to upper version of Angular
  constructor(
    private state: State<GanttState>,
    private restService: GanttChartRestService,
    private waiting: WaitingContext,
    @Optional() @Self() private timeline: GanttChartTimelineDirective,
    @Optional() @Self() @Inject(GANTT_CHART_CONTROLLER) private controller: GanttChartController,
    @Optional() @Self() @Inject(GANTT_CHART_ACCESSOR) private accessors?: GanttChartAccessor[],
  ) {
  }

  @HostListener('document:fullscreenchange', ['$event'])
  onKeyPress(): void {
    if (!this.isFullScreen()) {
      this.getFullscreenElement()?.classList.remove('fullscreen');
    }
  }

  ngAfterViewInit(): void {
    this.timeline.setAccessors(this.accessors || []);
    this.timeline.init(this.ganttEl.nativeElement);
    this.setupController();
  }

  getFullscreenElement(): any {
    return document.getElementsByTagName('gh-gantt-chart-timeline')[0];
  }

  openFullscreen(elem: any): void {
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    }
  }

  isFullScreen(): boolean {
    return !!document.fullscreenElement;
  }

  onFullScreen(): void {
    if (!this.isFullScreen()) {
      this.openFullscreen(this.getFullscreenElement());
      this.getFullscreenElement()?.classList?.add('fullscreen');
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      this.getFullscreenElement()?.classList?.remove('fullscreen');
    }
  }

  onPrintPdf(isA4?: boolean): void {
    const additionalSettings = {} as any;
    if (isA4) {
      additionalSettings.merge_pages = true;
      additionalSettings.fixed_headers = true;
      additionalSettings.format = 'A4';
    }
    const convertScheduleDate = this.timeline.gantt.date.date_to_str('%d-%m-%Y');
    const convertTaskDate = this.timeline.gantt.date.date_to_str('%Y-%m-%d');
    const tasks = this.state.project(queryGanttVisibleTaskSeq);
    const endDateSchedule = Math.max(...tasks.map((item) => item.endDate.getTime()));
    const startDateSchedule = Math.min(...tasks.map((item) => item.endDate.getTime()));
    const getTaskDate = (date: Date): Date => {
      if (!this.isTemplate) {
        return date;
      }
      const cloned = cloneDate(date);
      cloned.setFullYear(date.getFullYear() + 50);
      return cloned;
    };
    const mappedData = {
      skin: 'redtheme',
      name: `${this.name.split(' ').join('_')}.pdf`,
      start: !this.isTemplate ? convertScheduleDate(shiftDays(new Date(startDateSchedule), -1)) : null,
      end: !this.isTemplate ? convertScheduleDate(addDays(new Date(endDateSchedule), 2)) : null,
      additional_settings: additionalSettings,
      version: this.timeline.gantt.version,
      config : this.isTemplate ?  ganttExportConfig : getScheduleConfig(),
      data: {
        data: tasks.map((item: GanttTask) => {
          const startDate = getTaskDate(item.startDate);
          const endDate = getTaskDate(item.endDate);
          const text = item.name;
          return {
            id: item.taskId,
            progress: item.completionPct ?  item.completionPct / 100 : 0,
            parent: item.parentTaskId,
            $2        : text,
            $4        : '',
            text,
            duration: item.duration,
            open: item.opened,
            resource: item.resource?.name || '',
            taskNumber: item.taskNumber,
            type: this.state.value.tasksByParent[item.taskId] ? 'project' : 'task',
            start_date: `${convertTaskDate(startDate)}+00:00`,
            end_date  : `${convertTaskDate(endDate)}+23:59`,
          };
        }),
        links: this.timeline.gantt.getLinks(),
      },
    };

    const process = this.waiting.start(WaitingMode.Hidden);
    this.restService.printPdf(mappedData).subscribe((result) => {
      process.stop();
      const file = new Blob([result], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL);
    },                                              () => {
      process.stop();
    });
  }

  changeView(mode: GanttChartView): void {
    this.controller.setView(mode);
    this.scale = 1;
  }

  onShowCurrentDate(): void {
    const currentDate = this.state.project(queryGanttCurrentDate);
    if (currentDate) {
      this.timeline.showDate(currentDate);
    }
  }

  private setupController(): void {
    if (this.controller) {
      this.registerZoomHandler();
    }
  }

  private registerZoomHandler(): void {
    const scaleBar = document.querySelector('.gantt_task_scale')!; // tslint:disable-line:no-non-null-assertion

    fromEvent$(scaleBar, 'wheel')
    .subscribe((e: WheelEvent) => {
      const shift = Math.sign(e['wheelDelta'] ? e['wheelDelta'] : -1 * e.deltaY);
      if (this.controller.setScale) {
        const scale = clamp(getNextScale(this.scale, shift), this.controller.minScale, this.controller.maxScale);
        this.scale = scale;
        this.controller.setScale(scale);
      }
    });
  }
}
